import type { DesignTokens } from '@/handshake/protocol.js';

/**
 * Maps the host's `borderRadius` preset to the trio of CSS radius values
 * (`--radius`, `--radius-sm`, `--radius-lg`). Mirrors the Kadanza DAM widget's
 * `TenantThemeContext` so both apps scale their corners identically.
 */
const BORDER_RADIUS_MAP: Record<string, { radius: string; sm: string; lg: string }> = {
  sm: { radius: '0.25rem', sm: '0.125rem', lg: '0.5rem' },
  md: { radius: '0.5rem', sm: '0.25rem', lg: '0.75rem' },
  lg: { radius: '0.75rem', sm: '0.5rem', lg: '1rem' },
  xl: { radius: '1rem', sm: '0.75rem', lg: '1.25rem' },
};

/**
 * Converts a `#rrggbb` colour to the `H S% L%` string ShadCN expects for
 * CSS custom properties (no commas).
 *
 * Mirrors `kadanza-saas/extension-page-example/src/utils/hexToHsl.ts` from
 * `EXTENSION_GUIDE.md` §7.3.
 */
export function hexToHsl(hex: string): string {
  const normalized = hex.replace(/^#/, '');
  if (normalized.length !== 6) return '0 0% 50%';

  const r = parseInt(normalized.substring(0, 2), 16) / 255;
  const g = parseInt(normalized.substring(2, 4), 16) / 255;
  const b = parseInt(normalized.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const APPLIED_VARS = ['--primary', '--ring', '--radius', '--radius-sm', '--radius-lg'] as const;

export function applyDesignTokens(tokens: DesignTokens) {
  const root = document.documentElement;

  if (tokens.primaryColor) {
    const hsl = hexToHsl(tokens.primaryColor);
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
  }

  if (tokens.fontFamily) {
    document.body.style.fontFamily = `"${tokens.fontFamily}", sans-serif`;
  }

  const radius = BORDER_RADIUS_MAP[tokens.borderRadius];
  if (radius) {
    root.style.setProperty('--radius', radius.radius);
    root.style.setProperty('--radius-sm', radius.sm);
    root.style.setProperty('--radius-lg', radius.lg);
  }
}

export function clearDesignTokens() {
  const root = document.documentElement;
  for (const prop of APPLIED_VARS) {
    root.style.removeProperty(prop);
  }
  document.body.style.removeProperty('font-family');
}

export const DEFAULT_DESIGN_TOKENS: DesignTokens = {
  primaryColor: '#5b6bcc',
  fontFamily: 'Source Sans 3',
  borderRadius: 'md',
};
